import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { Check, Globe } from "lucide-react";

const STORAGE_KEY = "ilajly_lang_chosen";

export function FirstRunLanguagePicker() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string>(i18n.language?.split("-")[0] || "en");

  useEffect(() => {
    try {
      const chosen = localStorage.getItem(STORAGE_KEY);
      if (!chosen) setOpen(true);
    } catch {
      // ignore — show picker anyway? safer to skip
    }
  }, []);

  const apply = () => {
    i18n.changeLanguage(selected);
    try {
      localStorage.setItem("ilajly_lang", selected);
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) apply(); }}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Globe className="w-5 h-5 text-primary" />
            {t("home.chooseLanguage", "Choose your language")}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t("home.languageDescription", "You can change this anytime in My Space.")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-2">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = selected === lang.code;
            return (
              <button
                key={lang.code}
                onClick={() => setSelected(lang.code)}
                className={`w-full flex items-center justify-between p-3 rounded-xl border transition-colors ${
                  active
                    ? "border-primary bg-primary/10"
                    : "border-border bg-background hover:bg-muted"
                }`}
              >
                <div className="text-left">
                  <div className="text-base font-semibold text-foreground">{lang.nativeLabel}</div>
                  <div className="text-xs text-muted-foreground">{lang.label}</div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  active ? "border-primary bg-primary" : "border-muted-foreground/40"
                }`}>
                  {active && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
              </button>
            );
          })}
        </div>

        <Button onClick={apply} size="lg" className="w-full mt-2">
          {t("common.apply", "Apply")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
